// ============================================================================
// Entity: Roles
// Key: roles
// Kind: entity
// Generated file. Do not edit manually.
// ============================================================================

import {
	getPermissionsListQueryKey,
	getRolesDetailQueryKey,
	getRolesDetailQueryOptions,
	getRolesListQueryKey,
	getRolesListQueryOptions,
	getRolesRelationsPermissionsAssignedQueryKey,
	getRolesRelationsPermissionsAssignedQueryOptions,
	getRolesRelationsPermissionsAvailableQueryKey,
	getRolesRelationsPermissionsAvailableQueryOptions,
	getRolesRelationsUsersAssignedQueryKey,
	getRolesRelationsUsersAssignedQueryOptions,
	getRolesRelationsUsersAvailableQueryKey,
	getRolesRelationsUsersAvailableQueryOptions,
	getUsersListQueryKey,
	rolesCreate,
	rolesDelete,
	rolesRelationsPermissionsAssign,
	rolesRelationsPermissionsUnassign,
	rolesRelationsUsersAssign,
	rolesRelationsUsersUnassign,
	rolesUpdate,
} from '../api/backstage'
// #region Imports
import type {
	CreateOrUpdateRoleRequest,
	PagedResultOfPermissionSummary,
	PagedResultOfRolePermissionDto,
	PagedResultOfRoleSummary,
	PagedResultOfUserDto,
	PagedResultOfUserRoleDto,
	RolePermissionDto,
	RoleSummary,
	UserRoleDto,
} from '../api/backstage.schemas'
import type { EntityConfig, ListQueryParams } from './entity-config.types'
import {
	defineMutationEndpoint,
	defineQueryEndpoint,
} from './entity-config.types'

// #endregion

// #region Entity Endpoints
const rolesEntityEndpoints = {
	create: defineMutationEndpoint<
		RoleSummary,
		CreateOrUpdateRoleRequest,
		undefined
	>({
		key: 'roles.create',
		method: 'POST',
		path: '/admin/roles',
		mutationOptions: () => ({
			mutationFn: (variables: { data: CreateOrUpdateRoleRequest }) =>
				rolesCreate(variables.data),
		}),
		invalidates: [(_context) => getRolesListQueryKey()],
	}),
	delete: defineMutationEndpoint<void, undefined, { roleId: number }>({
		key: 'roles.delete',
		method: 'DELETE',
		path: '/admin/roles/{roleId}',
		pathParams: ['roleId'] as const,
		mutationOptions: () => ({
			mutationFn: (variables: { roleId: number | string | null | undefined }) =>
				rolesDelete(variables.roleId),
		}),
		invalidates: [(_context) => getRolesListQueryKey()],
	}),
	detail: defineQueryEndpoint<RoleSummary, { roleId: number }, undefined>({
		key: 'roles.detail',
		method: 'GET',
		path: '/admin/roles/{roleId}',
		pathParams: ['roleId'] as const,
		queryKey: (args) => getRolesDetailQueryKey(args?.pathParams?.roleId),
		queryOptions: (args) =>
			getRolesDetailQueryOptions(args?.pathParams?.roleId),
	}),
	list: defineQueryEndpoint<
		PagedResultOfRoleSummary,
		undefined,
		ListQueryParams
	>({
		key: 'roles.list',
		method: 'GET',
		path: '/admin/roles',
		queryKey: (args) => getRolesListQueryKey(args?.queryParams),
		queryOptions: (args) => getRolesListQueryOptions(args?.queryParams),
	}),
	update: defineMutationEndpoint<
		RoleSummary,
		CreateOrUpdateRoleRequest,
		{ roleId: number }
	>({
		key: 'roles.update',
		method: 'PUT',
		path: '/admin/roles/{roleId}',
		pathParams: ['roleId'] as const,
		mutationOptions: () => ({
			mutationFn: (variables: {
				roleId: number | string | null | undefined
				data: CreateOrUpdateRoleRequest
			}) => rolesUpdate(variables.roleId, variables.data),
		}),
		invalidates: [
			(_context) => getRolesListQueryKey(),
			(context) => getRolesDetailQueryKey(context.entityId ?? context.ownerId),
		],
	}),
} as const
// #endregion

// #region Relations
// #region Relation: Permissions
// Target Entity: permissions
const rolesPermissionsRelation = {
	key: 'permissions',
	targetEntity: 'permissions',
	endpoints: {
		assign: defineMutationEndpoint<
			RolePermissionDto[],
			(number | string)[],
			{ roleId: number }
		>({
			key: 'roles.relations.permissions.assign',
			method: 'POST',
			path: '/admin/roles/{roleId}/permissions',
			pathParams: ['roleId'] as const,
			mutationOptions: () => ({
				mutationFn: (variables: {
					roleId: number | string | null | undefined
					data: (number | string)[]
				}) => rolesRelationsPermissionsAssign(variables.roleId, variables.data),
			}),
			invalidates: [
				(context) =>
					getRolesRelationsPermissionsAssignedQueryKey(
						context.ownerId ?? context.entityId,
					),
				(context) =>
					getRolesRelationsPermissionsAvailableQueryKey(
						context.ownerId ?? context.entityId,
					),
				(_context) => getRolesListQueryKey(),
				(_context) => getPermissionsListQueryKey(),
			],
		}),
		assigned: defineQueryEndpoint<
			PagedResultOfRolePermissionDto,
			{ roleId: number },
			ListQueryParams
		>({
			key: 'roles.relations.permissions.assigned',
			method: 'GET',
			path: '/admin/roles/{roleId}/permissions',
			pathParams: ['roleId'] as const,
			queryKey: (args) =>
				getRolesRelationsPermissionsAssignedQueryKey(
					args?.pathParams?.roleId,
					args?.queryParams,
				),
			queryOptions: (args) =>
				getRolesRelationsPermissionsAssignedQueryOptions(
					args?.pathParams?.roleId,
					args?.queryParams,
				),
		}),
		available: defineQueryEndpoint<
			PagedResultOfPermissionSummary,
			{ roleId: number },
			ListQueryParams
		>({
			key: 'roles.relations.permissions.available',
			method: 'GET',
			path: '/admin/roles/{roleId}/assignable-permissions',
			pathParams: ['roleId'] as const,
			queryKey: (args) =>
				getRolesRelationsPermissionsAvailableQueryKey(
					args?.pathParams?.roleId,
					args?.queryParams,
				),
			queryOptions: (args) =>
				getRolesRelationsPermissionsAvailableQueryOptions(
					args?.pathParams?.roleId,
					args?.queryParams,
				),
		}),
		unassign: defineMutationEndpoint<
			void,
			(number | string)[],
			{ roleId: number }
		>({
			key: 'roles.relations.permissions.unassign',
			method: 'DELETE',
			path: '/admin/roles/{roleId}/permissions',
			pathParams: ['roleId'] as const,
			mutationOptions: () => ({
				mutationFn: (variables: {
					roleId: number | string | null | undefined
					data: (number | string)[]
				}) =>
					rolesRelationsPermissionsUnassign(variables.roleId, variables.data),
			}),
			invalidates: [
				(context) =>
					getRolesRelationsPermissionsAssignedQueryKey(
						context.ownerId ?? context.entityId,
					),
				(context) =>
					getRolesRelationsPermissionsAvailableQueryKey(
						context.ownerId ?? context.entityId,
					),
				(_context) => getRolesListQueryKey(),
				(_context) => getPermissionsListQueryKey(),
			],
		}),
	},
} as const
// #endregion

// #region Relation: Users
// Target Entity: users
const rolesUsersRelation = {
	key: 'users',
	targetEntity: 'users',
	endpoints: {
		assign: defineMutationEndpoint<
			UserRoleDto[],
			(number | string)[],
			{ roleId: number }
		>({
			key: 'roles.relations.users.assign',
			method: 'POST',
			path: '/admin/roles/{roleId}/users',
			pathParams: ['roleId'] as const,
			mutationOptions: () => ({
				mutationFn: (variables: {
					roleId: number | string | null | undefined
					data: (number | string)[]
				}) => rolesRelationsUsersAssign(variables.roleId, variables.data),
			}),
			invalidates: [
				(context) =>
					getRolesRelationsUsersAssignedQueryKey(
						context.ownerId ?? context.entityId,
					),
				(context) =>
					getRolesRelationsUsersAvailableQueryKey(
						context.ownerId ?? context.entityId,
					),
				(_context) => getRolesListQueryKey(),
				(_context) => getUsersListQueryKey(),
			],
		}),
		assigned: defineQueryEndpoint<
			PagedResultOfUserRoleDto,
			{ roleId: number },
			ListQueryParams
		>({
			key: 'roles.relations.users.assigned',
			method: 'GET',
			path: '/admin/roles/{roleId}/users',
			pathParams: ['roleId'] as const,
			queryKey: (args) =>
				getRolesRelationsUsersAssignedQueryKey(
					args?.pathParams?.roleId,
					args?.queryParams,
				),
			queryOptions: (args) =>
				getRolesRelationsUsersAssignedQueryOptions(
					args?.pathParams?.roleId,
					args?.queryParams,
				),
		}),
		available: defineQueryEndpoint<
			PagedResultOfUserDto,
			{ roleId: number },
			ListQueryParams
		>({
			key: 'roles.relations.users.available',
			method: 'GET',
			path: '/admin/roles/{roleId}/assignable-users',
			pathParams: ['roleId'] as const,
			queryKey: (args) =>
				getRolesRelationsUsersAvailableQueryKey(
					args?.pathParams?.roleId,
					args?.queryParams,
				),
			queryOptions: (args) =>
				getRolesRelationsUsersAvailableQueryOptions(
					args?.pathParams?.roleId,
					args?.queryParams,
				),
		}),
		unassign: defineMutationEndpoint<
			void,
			(number | string)[],
			{ roleId: number }
		>({
			key: 'roles.relations.users.unassign',
			method: 'DELETE',
			path: '/admin/roles/{roleId}/users',
			pathParams: ['roleId'] as const,
			mutationOptions: () => ({
				mutationFn: (variables: {
					roleId: number | string | null | undefined
					data: (number | string)[]
				}) => rolesRelationsUsersUnassign(variables.roleId, variables.data),
			}),
			invalidates: [
				(context) =>
					getRolesRelationsUsersAssignedQueryKey(
						context.ownerId ?? context.entityId,
					),
				(context) =>
					getRolesRelationsUsersAvailableQueryKey(
						context.ownerId ?? context.entityId,
					),
				(_context) => getRolesListQueryKey(),
				(_context) => getUsersListQueryKey(),
			],
		}),
	},
} as const
// #endregion
// #endregion

// #region Entity Config Export
export const rolesEntityConfig = {
	key: 'roles',
	kind: 'entity',
	entity: {
		idField: 'id',
		idParam: 'roleId',
		routePath: '/roles',
	},
	endpoints: rolesEntityEndpoints,
	relations: {
		permissions: rolesPermissionsRelation,
		users: rolesUsersRelation,
	},
} as const satisfies EntityConfig<'roles'>
// #endregion
